import { normalizeKnowledgeFacts } from '../knowledgeItems/knowledgeFactNormalizer';
import { KNOWLEDGE_ITEM_TYPES } from '../knowledgeItems/knowledgeItemModel';
import { CARE_LENS, getCareLensKey } from '../../utils/careLens';
import { normalizeChapterProvenance } from './chapterProvenance';
import { resolveKnowledgeSourceAction } from './knowledgeSourceAction';
import { buildProfileSourcePresentation } from './profileSourcePresentation';
import {
  PROFILE_PLACEMENT_CHAPTERS,
  isExplicitlyPlacedInAnotherChapter,
  isProfileFactLifecycleVisible,
} from './profilePlacementMetadata';
import { isKnowledgeFactProjectedToChapter } from './profileKnowledgeProjection';
import { PROFILE_CHAPTER_TITLES } from '../../constants/profileChapterTitles';
import {
  isStructuredAllergyFact,
  isStructuredMedicationFact,
} from './structuredManagerBoundaries';

export const HEALTH_CARE_DOMAIN_ID = 'DM_07';

const GROUPS = Object.freeze({
  health_context: 'Health conditions and care context',
  medications_treatments: 'Treatments and care routines',
  nutrition: 'Eating, drinking, and nutrition',
  sleep_rest: 'Sleep and rest needs',
  mobility_support: 'Mobility and physical support',
  symptoms_monitoring: 'Symptoms and ongoing monitoring',
  professional_guidance: 'Care plans and professional guidance',
  equipment_supplies: 'Equipment and supplies',
});

const DEFAULT_GROUP_ORDER = Object.freeze(Object.keys(GROUPS));
const cleanText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();
const getFactValue = (fact = {}) => cleanText(fact.summary || fact.title);
const valueKey = (value = '') => cleanText(value).toLowerCase().replace(/[.]+$/, '');

const contentHash = (value = '') => {
  let hash = 2166136261;
  for (const character of valueKey(value)) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
};

const isEpisodicObservation = (value = '') => (
  /\b(?:today|yesterday|last night|this morning|once|one time|one day|one dose|one episode|one measurement|for the first time|on (?:monday|tuesday|wednesday|thursday|friday|saturday|sunday))\b/i
    .test(value)
);

const isSafetyPrecaution = (value = '') => (
  /\b(?:keep (?:medications?|pathways?|doors?) (?:locked|clear|secured)|fall risk|emergency plan|supervis|out of reach|traffic danger|chok(?:e|ing) risk)\b/i
    .test(value)
);

const classifyFact = (value = '') => {
  const text = valueKey(value);
  if (isSafetyPrecaution(text)) return '';
  if (/\b(?:treatment|wound care|dressing change|therapy exercises?|dialysis)\b/.test(text)
    && /\b(?:uses?|apply|applied|follow|after|before|daily|routine|plan|instructions?)\b/.test(text)) return 'medications_treatments';
  if (/\b(?:meals?|foods?|diet|dietary|nutrition|drink|fluid|hydrat|handled cup|soft and easy to chew|feeding)\b/.test(text)
    && /\b(?:needs?|should|best|track|monitor|use|uses|drink|eat|consistent|soft|handled|support|plan|routine)\b/.test(text)) return 'nutrition';
  if (/\b(?:sleep|sleeping|bedtime|overnight|rest)\b/.test(text)
    && /\b(?:needs?|uses?|usually|while|support|care|oxygen|position|after)\b/.test(text)) return 'sleep_rest';
  if (/\b(?:follow|care plan|therapist|therapy plan|clinician|professional guidance|provider instructions?)\b/.test(text)
    && /\b(?:plan|instructions?|guidance|directed|recommended|transfer|care)\b/.test(text)) return 'professional_guidance';
  if (/\b(?:transfer|standing|mobility|physical support|two-person|hands-on support|walker)\b/.test(text)
    && /\b(?:needs?|assistance|support|instructions?|uses? .+ (?:for|when|during)|longer distances?)\b/.test(text)) return 'mobility_support';
  if (/\b(?:track|monitor|record|care log|baseline|usual symptom|ongoing symptom)\b/.test(text)
    && /\b(?:bowel|breath|pain|symptom|intake|output|movement|oxygen|glucose|temperature|blood pressure|weight|skin)\b/.test(text)) return 'symptoms_monitoring';
  if (/\b(?:equipment|supplies|hearing aids?|oxygen|wound dressing|handled cup|braces?|wheelchair)\b/.test(text)
    && /\b(?:needs?|uses?|wears?|available|required|during the day|for longer)\b/.test(text)) return 'equipment_supplies';
  if (/\b(?:has|diagnosed with|health condition|care context)\b/.test(text)
    && /\b(?:affects? daily care|caregiver should know|and (?:meals?|care|support|treatment))\b/.test(text)) return 'health_context';
  return '';
};

const TYPE_GROUPS = Object.freeze({
  [KNOWLEDGE_ITEM_TYPES.MEDICATION]: new Set(['medications_treatments']),
  [KNOWLEDGE_ITEM_TYPES.FEEDING]: new Set(['nutrition']),
  [KNOWLEDGE_ITEM_TYPES.SLEEP]: new Set(['sleep_rest']),
  [KNOWLEDGE_ITEM_TYPES.BASELINE]: new Set(['symptoms_monitoring']),
  [KNOWLEDGE_ITEM_TYPES.CONDITION]: new Set(['health_context']),
  [KNOWLEDGE_ITEM_TYPES.INSTRUCTION]: new Set(['medications_treatments', 'nutrition', 'sleep_rest', 'mobility_support', 'symptoms_monitoring', 'professional_guidance', 'equipment_supplies']),
  [KNOWLEDGE_ITEM_TYPES.SUPPORT]: new Set(['nutrition', 'sleep_rest', 'mobility_support', 'professional_guidance', 'equipment_supplies']),
  [KNOWLEDGE_ITEM_TYPES.ROUTINE]: new Set(['medications_treatments', 'nutrition', 'sleep_rest']),
});

const isEligibleKnowledgeFact = (fact = {}) => {
  if (!isProfileFactLifecycleVisible(fact)) return false;
  if (isExplicitlyPlacedInAnotherChapter(fact, PROFILE_PLACEMENT_CHAPTERS.HEALTH_CARE)) return false;
  if (isStructuredMedicationFact(fact) || isStructuredAllergyFact(fact)) return false;
  if (isKnowledgeFactProjectedToChapter(fact, PROFILE_PLACEMENT_CHAPTERS.HEALTH_CARE, { includeFallback: false })) return true;
  if (fact.needsQuickCheck && fact.profileVisibilityOverride !== 'pin') return false;
  if (isEpisodicObservation(getFactValue(fact))) return false;
  const groupId = classifyFact(getFactValue(fact));
  return Boolean(groupId && TYPE_GROUPS[fact.knowledgeType]?.has(groupId));
};

const normalizeLegacyValues = (value) => (
  (Array.isArray(value) ? value : [value])
    .map((item) => cleanText(typeof item === 'string' ? item : item?.label || item?.name || item?.title || item?.value))
    .filter(Boolean)
);

const buildSyntheticFact = ({ value, source, knowledgeType }) => ({
  id: `legacy-health-${source}-${contentHash(value)}`,
  title: value,
  summary: value,
  knowledgeType,
  profileField: '',
  factStatus: 'current',
  profileVisibilityOverride: null,
  confidence: 'high',
  needsQuickCheck: false,
  sourceRefs: [],
  responseGuidance: null,
  resolverEligible: true,
  synthetic: true,
});

// Retire these adapters after legacy medical Profile fields have a reviewed Knowledge migration.
const buildLegacyHealthFacts = (child = {}) => [
  ...normalizeLegacyValues(child.medicalProfile?.dietaryRestrictions)
    .map((value) => buildSyntheticFact({ value: `Dietary need: ${value}`, source: 'diet', knowledgeType: KNOWLEDGE_ITEM_TYPES.FEEDING })),
  ...normalizeLegacyValues(child.medicalProfile?.sleepIssues)
    .map((value) => buildSyntheticFact({ value: `Sleep care need: ${value}`, source: 'sleep', knowledgeType: KNOWLEDGE_ITEM_TYPES.SLEEP })),
];

const deduplicateFacts = (facts = []) => {
  const output = [];
  const indexByValue = new Map();
  facts.forEach((fact) => {
    const key = valueKey(getFactValue(fact));
    if (!key) return;
    const existingIndex = indexByValue.get(key);
    if (existingIndex === undefined) {
      indexByValue.set(key, output.length);
      output.push(fact);
    } else if (output[existingIndex].synthetic && !fact.synthetic) {
      output[existingIndex] = fact;
    }
  });
  return output;
};

const getGroupOrder = (lens) => {
  if (lens === CARE_LENS.AUTISM) return ['nutrition', 'sleep_rest', 'medications_treatments', 'professional_guidance', 'mobility_support', 'symptoms_monitoring', 'equipment_supplies', 'health_context'];
  if (lens === CARE_LENS.DEMENTIA) return ['medications_treatments', 'mobility_support', 'nutrition', 'sleep_rest', 'symptoms_monitoring', 'equipment_supplies', 'professional_guidance', 'health_context'];
  if (lens === CARE_LENS.HOSPICE) return ['symptoms_monitoring', 'medications_treatments', 'equipment_supplies', 'professional_guidance', 'nutrition', 'sleep_rest', 'mobility_support', 'health_context'];
  return DEFAULT_GROUP_ORDER;
};

const priorityForGroup = (groupId = '') => ({
  medications_treatments: 900,
  mobility_support: 875,
  nutrition: 850,
  professional_guidance: 825,
  symptoms_monitoring: 800,
  equipment_supplies: 775,
  sleep_rest: 750,
  health_context: 500,
}[groupId] || 0);

// Medication and allergy records are excluded above because their structured
// managers are authoritative. Remaining Health & Care facts reuse the shared
// source-action classifier.
const resolveFactSourceAction = (fact) => {
  if (fact.synthetic) return undefined;
  return resolveKnowledgeSourceAction(fact);
};

const buildProvenance = (facts = []) => normalizeChapterProvenance(
  facts.filter((fact) => !fact.synthetic).flatMap((fact) => fact.sourceRefs || []),
  'health-care-source'
);

export const buildHealthCareDomain = ({ child = {}, knowledgeFacts = [], careContext = child.careContext || {}, ageGroup = careContext?.ageGroup || '' } = {}) => {
  const lens = getCareLensKey({ selectedConditions: child.conditions || [], careContext: careContext || child.careContext || {} });
  const groupOrder = getGroupOrder(lens);
  const realFacts = normalizeKnowledgeFacts(knowledgeFacts).filter(isEligibleKnowledgeFact).map((fact) => ({ ...fact, synthetic: false }));
  const rankedFacts = deduplicateFacts([...buildLegacyHealthFacts(child), ...realFacts])
    .map((fact, index) => ({
      fact,
      groupId: classifyFact(getFactValue(fact)) || (
        fact.profileChapterId === PROFILE_PLACEMENT_CHAPTERS.HEALTH_CARE
          ? 'health_context'
          : ''
      ),
      index,
    }))
    .filter((item) => item.groupId)
    .sort((left, right) => priorityForGroup(right.groupId) - priorityForGroup(left.groupId)
      || Number(right.fact.profileVisibilityOverride === 'pin') - Number(left.fact.profileVisibilityOverride === 'pin')
      || groupOrder.indexOf(left.groupId) - groupOrder.indexOf(right.groupId)
      || left.index - right.index);
  const groups = groupOrder.map((groupId) => ({
    id: groupId,
    title: GROUPS[groupId],
    entries: rankedFacts.filter((item) => item.groupId === groupId).map(({ fact }) => {
      const sourceAction = resolveFactSourceAction(fact);
      return {
        id: `entry-${fact.id}`,
        knowledgeFactId: sourceAction?.ownership === 'manager_reference' ? undefined : fact.id,
        headline: getFactValue(fact).replace(/[.]+$/, ''),
        explanation: `This is confirmed ${GROUPS[groupId].toLowerCase()} information for day-to-day care.`,
        storyText: getFactValue(fact),
        responseGuidance: fact.responseGuidance || undefined,
        provenance: buildProvenance([fact]),
        sourcePresentation: sourceAction?.ownership === 'manager_reference'
          ? undefined
          : buildProfileSourcePresentation(fact),
        sourceAction,
      };
    }),
  })).filter((group) => group.entries.length);
  return {
    id: 'health-care',
    title: PROFILE_CHAPTER_TITLES.HEALTH_CARE,
    description: 'Ongoing health conditions, care needs, treatments, and clinical guidance.',
    caregiverQuestion: 'What health and care information should I know?',
    briefing: {
      items: rankedFacts.slice(0, 3).map(({ fact }) => ({ id: `briefing-${fact.id}`, text: getFactValue(fact).replace(/[.]+$/, ''), explainedByEntryIds: [`entry-${fact.id}`] })),
      provenance: buildProvenance(rankedFacts.map((item) => item.fact)),
    },
    handbook: { groups },
    emptyState: {
      title: 'What health and care information should I know?',
      description: 'Add treatments, nutrition needs, mobility support, monitoring, equipment, or professional care guidance.',
    },
    metadata: { domainId: HEALTH_CARE_DOMAIN_ID, careFocus: lens, ageGroup: ageGroup || careContext?.ageGroup || 'unknown', contractVersion: '1' },
  };
};
